import { Divider, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { _deleteCompany } from '@/api/deal/mutations';
import AlertTriangleIcon from '@/assets/svg/ui-elements/alert-triangle-outline-orange.svg';
import DeleteIcon from '@/assets/svg/ui-elements/delete.svg';
import { Tooltip } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { CustomButton } from '@/shared/components/custom-button';
import CustomModal from '@/shared/components/custom-modal';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';

export type DeleteDealCompanyModalProps = {
  companyId: string;
  companyName: string;
  offersCount: number;
};

const DELETE_DENIED_TOOLTIP = 'You do not have permission to delete this company (DealDelete).';

export const DeleteDealCompanyModal = () => {
  const { open, handleClose, props } = useGlobalModal('DELETE_DEAL_COMPANY_MODAL');
  const canDeleteCompany = useCan(Permission.DealDelete);
  const { mutate: deleteCompany, isPending } = useMutation({
    mutationFn: _deleteCompany,
    onSuccess: handleClose,
  });

  const handleDelete = () => {
    if (!canDeleteCompany || !props?.companyId) return;
    deleteCompany(props.companyId);
  };

  return (
    <CustomModal open={open} onClose={handleClose} withCloseButton title="Delete Company">
      <Stack flexDirection="column" gap="24px" mt="16px">
        <Divider />

        <Typography variant="body1" color="text.secondary">
          You are about to permanently delete{' '}
          <Typography component="span" variant="body1" color="text.primary" fontWeight={500}>
            {props?.companyName}
          </Typography>
          . This action cannot be undone.
        </Typography>

        {!!props?.offersCount && (
          <Stack
            flexDirection="column"
            gap="12px"
            p="12px"
            borderRadius="12px"
            sx={{ background: 'rgba(255, 255, 255, 0.05)' }}
          >
            <AlertTriangleIcon width={24} height={24} style={{ flexShrink: 0 }} />
            <Stack flexDirection="column" gap="8px">
              <Typography variant="body1" fontWeight={500} color="text.primary">
                This company has {props.offersCount} active offer
                {props.offersCount !== 1 ? 's' : ''}.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Deleting this company will also close all associated offers and they will no longer
                be visible to investors.
              </Typography>
            </Stack>
          </Stack>
        )}

        <Stack flexDirection="column" gap="8px">
          <Tooltip title={DELETE_DENIED_TOOLTIP} side="top" disabled={canDeleteCompany}>
            <DeniedReasonTrigger denied={!canDeleteCompany}>
              <CustomButton
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={isPending || !canDeleteCompany}
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  height: 40,
                  borderRadius: '8px',
                }}
              >
                <DeleteIcon width={16} height={16} color="#FFFFFF" />
                <Typography variant="body2">Delete Company</Typography>
              </CustomButton>
            </DeniedReasonTrigger>
          </Tooltip>
          <CustomButton
            onClick={handleClose}
            sx={{
              height: 40,
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.05)',
            }}
          >
            <Typography variant="body2">Cancel</Typography>
          </CustomButton>
        </Stack>
      </Stack>
    </CustomModal>
  );
};
